import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, signal, Service } from '@angular/core';
import { DataAccessCashRegisterService } from '@h5s/data-access-cash-register';
import { DataAccessInvoiceService } from '@h5s/data-access-invoice';
import { DataAccessZPrinterService } from '@h5s/data-access-z-printer';
import { DialogService, ToastService } from '@h5s/design-system';
import {
  CashRegister,
  CashRegisterCreditNote,
  CashRegisterReceipt,
  CashRegisterStatus,
  CreateCashRegister,
  DeviceType,
  InvoiceData,
  Manufacturer,
  NaturaType,
  PaperSize,
  QueryCashRegister,
  findFiscalConfigConflicts,
  getVatRatesUsedByDepartments,
  resolveExpectedVatIndex,
  SavePrintedDocumentData,
  UpdateCashRegister,
  UpsertZPrinterConfiguration,
  VatRate,
  ZPrinterDeviceType,
  ZPrinterError,
  ZPrinterFiscalConfigRequest,
  ZPrinterFiscalProtocol,
  ZPrinterHealthResponse,
  ZPrinterHealthStatus,
  ZPrinterManufacturer,
  ZPrinterPaperSize,
  ZPrinterPrintJob,
  ZPrinterTransactionStatus,
  ValidationErrorResponse,
  PrintJobDtoEntity,
  ZPrinterStatus,
} from '@h5s/entities';
import { FeatureLogService } from '@h5s/feature-log';
import { dateTimeToString, today } from '@h5s/util-date';
import {
  AppError,
  AppErrorBuilder,
  BackendErrorCode,
  isAppErrorWithBackendCause,
  Severity,
  UserFacingTranslationKey,
} from '@h5s/util-error';
import { EventBusService } from '@h5s/util-event';
import { LogInfoBuilder } from '@h5s/util-logger';
import { isVersionSupported } from '@h5s/util-object';
import { ActionType, OverlayResult } from '@h5s/util-overlay';
import { retryWithBackoff } from '@h5s/util-rxjs';
import { SupportedLanguages, TranslationService } from '@h5s/util-translate';
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  firstValueFrom,
  forkJoin,
  from,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { SavedPrinterDocumentDataEvent } from './events/save-invoice-data.event';
import { PrintReceiptResult } from './models/print-receipt-result';
import { FEATURE_CASH_REGISTER_EDIT_TRANSLATIONS } from './translations/feature-cash-register-edit.translations';
import { FEATURE_CASH_REGISTER_TRANSLATIONS } from './translations/feature-cash-register.translations';
import { DataAccessDepartmentService } from '@h5s/data-access-settings';
import { PrintDocumentContext } from './models/print-document-context';

const MANUFACTURER_MAP: Record<Manufacturer, ZPrinterManufacturer> = {
  [Manufacturer.Rch]: ZPrinterManufacturer.Rch,
  [Manufacturer.Custom]: ZPrinterManufacturer.Custom,
  [Manufacturer.Epson]: ZPrinterManufacturer.Epson,
  [Manufacturer.ItalRetail]: ZPrinterManufacturer.ItalRetail,
};

const PAPER_SIZE_MAP: Record<PaperSize, ZPrinterPaperSize> = {
  [PaperSize.MM80]: ZPrinterPaperSize.MM80,
  [PaperSize.MM57]: ZPrinterPaperSize.MM57,
};

export const DEVICE_TYPE_LABEL_MAP: Record<DeviceType, keyof typeof FEATURE_CASH_REGISTER_EDIT_TRANSLATIONS> = {
  [DeviceType.PRINTF]: 'printf',
  [DeviceType.ITALRETAIL]: 'italRetail',
  [DeviceType.CUSTOM]: 'custom',
  [DeviceType.EPSON_FP_81_90]: 'epson_fp_81_80',
  [DeviceType.PRINTF_7]: 'printf_7',
  [DeviceType.ITALRETAIL_7]: 'italRetail_7',
  [DeviceType.CUSTOM_7]: 'custom_7',
  [DeviceType.EPSON_FP_81_90_7]: 'epson_fp_81_90_7',
};

export const DEFAULT_PORT = '9100';
export const DEFAULT_ADDRESS = '192.168.1.0';
const ZBRIDGE_VERSION_TIMEOUT_MS = 5000;

export interface ContinueFlow {
  kind: 'continue';
  context: PrintReceiptContext;
}

export interface CompletedFlow {
  kind: 'completed';
  result: PrintReceiptResult;
}

type PrintReceiptContext = PrintDocumentContext<CashRegisterReceipt>;

type PrintCreditNoteContext = PrintDocumentContext<CashRegisterCreditNote>;

type PrintReceiptFlow = ContinueFlow | CompletedFlow;

type SaveFiscalDataOutcome = { alreadyPrinted: false; saveLinkedDataResult: boolean } | { alreadyPrinted: true };

@Service()
export class FeatureCashRegisterService {
  private readonly dataAccessCashRegisterService = inject(DataAccessCashRegisterService);
  private readonly dataAccessZPrinterService = inject(DataAccessZPrinterService);
  private readonly dataAccessInvoiceService = inject(DataAccessInvoiceService);
  private readonly toastService = inject(ToastService);
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslationService);
  private readonly eventBusService = inject(EventBusService);
  private readonly featureLogService = inject(FeatureLogService);
  private readonly dataAccessDepartmentService = inject(DataAccessDepartmentService);

  private readonly _zPrinterVersion = signal<string | null>(null);
  public readonly zPrinterVersion = computed(() => this._zPrinterVersion());

  /* Translations */
  private readonly translation = FEATURE_CASH_REGISTER_TRANSLATIONS;

  /** Casse dell'anagrafica, senza alcuno stato di connessione (vedi getCashRegistersWithStatus) */
  public searchCashRegisters(ownerCorporateDataId: number): Observable<CashRegister[]> {
    const queryCashRegister = new QueryCashRegister();
    queryCashRegister.ownerCorporateDataId = ownerCorporateDataId;

    return this.dataAccessCashRegisterService.searchCashRegister(queryCashRegister);
  }

  /**
   * Sincronizza le stampanti col gateway e ne verifica la raggiungibilità.
   * Ogni cassa viene emessa, con lo status valorizzato, non appena la sua stampante risponde: la più lenta non blocca le altre.
   */
  public getCashRegistersWithStatus(cashRegisters: CashRegister[]): Observable<CashRegister> {
    if (cashRegisters.length === 0) {
      return EMPTY;
    }

    // Controlliamo se lo ZPrinter è connesso
    return this.dataAccessZPrinterService.getPrinters().pipe(
      switchMap((printers) =>
        // Se connesso faccio la sync
        merge(
          ...cashRegisters.map((cr) =>
            this.dataAccessZPrinterService.syncPrinter(this.mapCashRegisterToUpsertPrinter(cr), printers).pipe(
              // Se la sync col gateway va bene, allora verifico la stampante fisica
              switchMap(() => this.setStatusFromPrinter(cr)),

              // Se fallisce la sync col gateway, assegno status gateway
              catchError((error: HttpErrorResponse) => {
                this.setGatewayStatus(cr, error);
                return of(cr);
              }),
            ),
          ),
        ),
      ),
      // Se fallisce, assegno status gateway disconnesso.
      // Scatta solo se getPrinters() fallisce, cioè prima che sia stata emessa una qualsiasi cassa (gli errori delle singole
      // stampanti sono già catturati sopra): se una cassa fosse già stata emessa, qui verrebbe emessa una seconda volta.
      catchError((error: HttpErrorResponse) => {
        cashRegisters.forEach((cr) => this.setStatusGatewayDisconnected(cr, error));
        return from(cashRegisters);
      }),
    );
  }

  /** Imposta lo stato gateway disconnesso */
  private setStatusGatewayDisconnected(cashRegister: CashRegister, error: HttpErrorResponse): void {
    console.error(
      new AppErrorBuilder('[FeatureCashRegisterService] Error syncing printer with gateway', Severity.ERROR)
        .addCause(error)
        .addInfo('cashRegisterId', String(cashRegister.id))
        .build(),
    );

    cashRegister.status = CashRegisterStatus.GatewayDisconnected;
  }

  /** Imposta lo stato della stampante */
  private setStatusFromPrinter(cashRegister: CashRegister): Observable<CashRegister> {
    return this.dataAccessZPrinterService.status(String(cashRegister.id)).pipe(
      map(() => {
        cashRegister.status = CashRegisterStatus.Active;
        return cashRegister;
      }),
      catchError((error) => {
        console.error(
          new AppErrorBuilder('[FeatureCashRegisterService] Error getting printer status', Severity.ERROR)
            .addCause(error)
            .addInfo('cashRegisterId', String(cashRegister.id))
            .build(),
        );

        cashRegister.status = CashRegisterStatus.Unreachable;
        return of(cashRegister);
      }),
    );
  }

  /** Imposta lo stato del gateway */
  private setGatewayStatus(cr: CashRegister, error: HttpErrorResponse): void {
    console.error(
      new AppErrorBuilder('[FeatureCashRegisterService] Error syncing printer with gateway', Severity.ERROR)
        .addCause(error)
        .addInfo('cashRegisterId', String(cr.id))
        .build(),
    );

    cr.status = error.status === 500 ? CashRegisterStatus.GatewayDisconnected : CashRegisterStatus.Outdated;
  }

  /** Mappa un CashRegister a una UpsertZPrinterConfiguration, in modo da poter fare upsert delle stampanti basandosi sui dati dei cash register */
  private mapCashRegisterToUpsertPrinter(cashRegister: CashRegister): UpsertZPrinterConfiguration {
    return new UpsertZPrinterConfiguration({
      id: String(cashRegister.id),
      ip: cashRegister.address,
      port: cashRegister.port ? Number(cashRegister.port) : Number(DEFAULT_PORT),
      name: cashRegister.description,
      manufacturer: this.mapManufacturer(cashRegister.cashRegisterType?.manufacturer),
      paper_size: this.mapPaperSize(cashRegister.cashRegisterType?.paperSize),
      device_type: ZPrinterDeviceType.PRINTER,
    });
  }

  // Traduce la marca da come la salviamo noi (Manufacturer: solo le 4 marche di casse fiscali che gestiamo)
  // a come la vuole il ZPrinterGateway (ZPrinterManufacturer: enum più ampia, condivisa anche con terminali
  // di pagamento e altri dispositivi che il bridge gestisce oltre alle stampanti fiscali). Stessa marca, due
  // enum diverse perché appartengono a due sistemi diversi.
  public mapManufacturer(manufacturer?: Manufacturer): ZPrinterManufacturer {
    return manufacturer ? MANUFACTURER_MAP[manufacturer] : ZPrinterManufacturer.Unknown;
  }

  /** Mappa la dimensione della carta del registratore di cassa alla dimensione della carta della stampante */
  private mapPaperSize(paperSize?: PaperSize): ZPrinterPaperSize | undefined {
    return paperSize ? PAPER_SIZE_MAP[paperSize] : undefined;
  }

  /** Recupera le opzioni per il tipo di dispositivo (device type) da mostrare nel form, mappando i valori dell'enum DeviceType in un record chiave-valore dove sia la chiave che il valore sono le stringhe rappresentanti i device type */
  public getDeviceTypeOptions(): Record<string, string> {
    return Object.values(DeviceType).reduce(
      (acc, dt) => {
        const key = DEVICE_TYPE_LABEL_MAP[dt];
        acc[dt] = FEATURE_CASH_REGISTER_EDIT_TRANSLATIONS[key] ?? dt;
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  /** Recupera cashRegister per ID */
  public getCashRegisterById(id: number): Observable<CashRegister> {
    return this.dataAccessCashRegisterService.getCashRegisterById(id);
  }

  /** Crea un nuovo cashRegister */
  public createCashRegister(payload: CreateCashRegister): Observable<number> {
    return this.dataAccessCashRegisterService.createCashRegister(payload).pipe(
      tap(() => {
        this.toastService.showSuccess();
      }),
      catchError((error) => {
        throw new AppErrorBuilder('[FeatureCashRegisterService] Error creating printer', Severity.ERROR)
          .addCause(error)
          .addInfo('payload', JSON.stringify(payload))
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  /** Aggiorna un cashRegister esistente */
  public updateCashRegisterById(payload: UpdateCashRegister): Observable<boolean> {
    return this.dataAccessCashRegisterService.updateCashRegister(payload).pipe(
      tap(() => {
        this.toastService.showSuccess();
      }),
      catchError((error) => {
        throw new AppErrorBuilder('[FeatureCashRegisterService] Error updating printer', Severity.ERROR)
          .addCause(error)
          .addInfo('payload', JSON.stringify(payload))
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  /** Elimina cashRegister per ID */
  public deleteCashRegisterById(id: number): Observable<boolean> {
    return this.dataAccessCashRegisterService.deleteCashRegisterById(id).pipe(
      tap(() => {
        this.toastService.showSuccess();
      }),
      catchError((error) => {
        throw new AppErrorBuilder('[FeatureCashRegisterService] Error deleting printer', Severity.ERROR)
          .addCause(error)
          .addInfo('id', id)
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  /**
   * Data un'invoice, restituisce il registratore di cassa sul quale effettuare la stampa in base a criteri multipli (in ordine di importanza):
   *
   *   - **Filtro per Username**: Supporta più username separati da ";". Utile per assegnare registratori specifici a utenti specifici
   *   - **Filtro per Indirizzo IP Locale**: Identifica il PC/terminale da cui si stampa. Assegna automaticamente il registratore di cassa
   *   fisicamente collegato
   *   - **Filtro per Blocco Fattura**: Supporta divisione per "blocchi" di fatturazione. Utile per hotel con più punti cassa
   *   - **Registratore Generico (Default)**: Se nessun filtro è configurato, usa il primo registratore trovato
   *
   * @param invoiceId id della fattura da stampare
   * @returns Ritorna un Observable di tipo CashRegister.
   * @throws {@link AppErrorBuilder} Se si verifica un errore durante la ricerca della stampante da usare
   */
  public resolveCashRegister(invoiceId: number): Observable<CashRegister> {
    return this.dataAccessCashRegisterService.resolveCashRegister(invoiceId).pipe(
      catchError((error) => {
        throw new AppErrorBuilder('[FeatureCashRegisterService] Error resolving cash register printer', Severity.ERROR)
          .addCause(error)
          .addInfo('invoiceId', invoiceId)
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  /**
   * Data un'invoice, costruisce la ricevuta di stampa da passare alla stampante fiscale
   * @param invoiceId
   * @param language
   * @returns Ritorna un Observable di tipo CashRegisterReceipt.
   * @throws {@link AppErrorBuilder} Se si verifica un errore durante la costruzione dello scontrino da mandare in stampa
   */
  public buildCashRegisterReceipt(
    invoiceId: number,
    language = SupportedLanguages.IT,
  ): Observable<CashRegisterReceipt> {
    return this.dataAccessCashRegisterService.buildCashRegisterReceipt(invoiceId, language).pipe(
      catchError((error) => {
        if (isAppErrorWithBackendCause(error, BackendErrorCode.DocumentAlreadyPrintedOnCashRegisterException)) {
          return this.loadAndShowAlreadyPrintedDocumentData(invoiceId).pipe(
            take(1),
            switchMap(() => throwError(() => error)),
          );
        }

        throw new AppErrorBuilder('[FeatureCashRegisterService] Error building cash register receipt', Severity.ERROR)
          .addCause(error)
          .addInfo('invoiceId', invoiceId)
          .addInfo('language', language)
          .build();
      }),
    );
  }

  public buildCashRegisterCreditNote(creditNoteInvoiceId: number): Observable<CashRegisterCreditNote> {
    return this.dataAccessCashRegisterService.buildCashRegisterCreditNote(creditNoteInvoiceId).pipe(
      catchError((error) => {
        if (isAppErrorWithBackendCause(error, BackendErrorCode.DocumentAlreadyPrintedOnCashRegisterException)) {
          return this.loadAndShowAlreadyPrintedDocumentData(creditNoteInvoiceId).pipe(
            take(1),
            switchMap(() => throwError(() => error)),
          );
        }

        throw new AppErrorBuilder(
          '[FeatureCashRegisterService] Error building cash register credit note',
          Severity.ERROR,
        )
          .addCause(error)
          .addInfo('creditNoteInvoiceId', creditNoteInvoiceId)
          .build();
      }),
    );
  }

  /**
   * Costruice i parametri per generare un documento di annullo partendo da degli InvoiceData
   */
  public buildCashRegisterCreditNoteFromInvoiceData(invoiceData: InvoiceData): Observable<CashRegisterCreditNote> {
    const result = new CashRegisterCreditNote();
    if (!invoiceData.cashRegisterClosure || !invoiceData.cashRegisterPrintTimestamp || !invoiceData.cashRegisterCode) {
      throw new AppErrorBuilder('[buildCashRegisterCreditNoteFromInvoiceData] Missing invoiceData')
        .addInfo('cashRegisterClosure', invoiceData.cashRegisterClosure ?? undefined)
        .addInfo(
          'cashRegisterPrintTimestamp',
          invoiceData.cashRegisterPrintTimestamp ? dateTimeToString(invoiceData.cashRegisterPrintTimestamp) : undefined,
        )
        .addInfo('cashRegisterCode', invoiceData.cashRegisterCode ?? undefined)
        .build();
    }

    result.closure = invoiceData.cashRegisterClosure;
    result.date = invoiceData.cashRegisterPrintTimestamp;
    result.documentNumber = invoiceData.cashRegisterCode;

    return of(result);
  }

  /**
   * Inserisce un documento nella coda di stampa per la stampante specificata.
   *
   * @param printerIdOrIp L'identificatore della stampante (printerId) o l'indirizzo IP della stampante (IP).
   * @param cashRegisterReceipt Ricevuta da stampare.
   * @returns Ritorna un Observable di tipo PrintJobDto.
   * @throws {@link AppErrorBuilder} Se si verifica un errore durante l'inserimento del documento nella coda di stampa.
   */
  public enqueueDocument(
    printerIdOrIp: string,
    cashRegisterReceipt: CashRegisterReceipt,
    invoiceId: number,
    progressive: number,
    block: string | null,
    manufacturer: ZPrinterManufacturer,
  ): Observable<ZPrinterPrintJob> {
    return this.dataAccessZPrinterService.enqueueDocument(printerIdOrIp, cashRegisterReceipt, manufacturer).pipe(
      catchError((error: unknown) => {
        this.validateZPrinterJobFromAppError(error, cashRegisterReceipt, invoiceId, progressive, block);

        if (!(error instanceof AppError)) {
          return throwError(() => error);
        }

        // Questo lo lasciamo in quanto, se è un errore non gestito all'interno del job, abbiamo un fallback
        throw new AppErrorBuilder('[FeatureCashRegisterService] Error enqueueing document', Severity.ERROR)
          .addCause(error)
          .addInfo('printerIdOrIp', printerIdOrIp)
          .addInfo('payload', JSON.stringify(cashRegisterReceipt))
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  private validateZPrinterJobFromAppError(
    error: unknown,
    cashRegisterReceipt: CashRegisterReceipt | CashRegisterCreditNote,
    invoiceId: number,
    progressive: number,
    block: string | null,
  ): void {
    if (!(error instanceof AppError)) {
      return;
    }

    // Se è già uno UserFacing o Validation, lanciamo quello
    if (error.hasUserFacingOrValidationErrorCause()) {
      throw error;
    }

    const bodyJson = error.info.get('responseBody') as string;
    if (bodyJson) {
      const body = JSON.parse(bodyJson) as ZPrinterPrintJob;

      // Controlliamo se viene ritornato un job
      if (this.hasPrintJob(body)) {
        this.checkJobResultForError(body, cashRegisterReceipt, invoiceId, progressive, block);
      }
    }
  }

  public enqueueCreditNote(
    printerIdOrIp: string,
    cashRegisterReceipt: CashRegisterCreditNote,
    invoiceId: number,
    progressive: number,
    block: string | null,
  ): Observable<ZPrinterPrintJob> {
    return this.dataAccessZPrinterService.enqueueCreditNote(printerIdOrIp, cashRegisterReceipt).pipe(
      catchError((error) => {
        this.validateZPrinterJobFromAppError(error, cashRegisterReceipt, invoiceId, progressive, block);

        // Questo lo lasciamo in quanto, se è un errore non gestito all'interno del job, abbiamo un fallback
        throw new AppErrorBuilder('[FeatureCashRegisterService] Error enqueueing document', Severity.ERROR)
          .addCause(error)
          .addInfo('printerIdOrIp', printerIdOrIp)
          .addInfo('payload', JSON.stringify(cashRegisterReceipt))
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  private hasPrintJob(error: unknown): error is PrintJobDtoEntity {
    return typeof error === 'object' && error !== null && ('printJobId' in error || 'print_job_id' in error);
  }

  /**
   * Esegue una stampa non fiscale di test per verificare la connessione con la stampante.
   * @param printerIdOrIp
   * @returns Ritorna un Observable di tipo PrintJobDto.
   */
  public testPrint(printerIdOrIp: string): Observable<ZPrinterPrintJob> {
    return this.dataAccessZPrinterService.enqueueTestPrint(printerIdOrIp).pipe(
      tap(() => {
        this.toastService.showSuccess();
      }),
      catchError((error) => {
        throw new AppErrorBuilder('[FeatureCashRegisterService] Error performing test print', Severity.ERROR)
          .addCause(error)
          .addInfo('printerIdOrIp', printerIdOrIp)
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  /**
   * Effettua la stampa di una ricevuta
   * @param invoiceId Id del documento
   * @param progressive Progressivo
   * @param block Blocco
   * @param isFiscal Indica se si è in modalità fiscale o meno
   * @param lang Lingua di stampa
   */
  public printReceipt(
    invoiceId: number,
    progressive: number,
    block: string | null,
    isFiscal: boolean,
    lang: SupportedLanguages,
  ): Observable<PrintReceiptResult> {
    const result = new PrintReceiptResult();

    // Partiamo controllando lo stato del Bridge
    return this.checkBridgeHealth().pipe(
      // Controlliamo se il documento è già stato stampato
      switchMap(() => this.isAlreadyPrinted(invoiceId)),
      switchMap((alreadyPrinted) => {
        // Se il documento è già stato stampato, usciamo
        if (alreadyPrinted) {
          result.isAlreadyPrinted = true;
          return of(result);
        }

        // Altrimenti proseguiamo costruendo il context che ci porteremo dietro per tutti i flussi di stampa
        return this.buildPrintContext$(invoiceId, lang).pipe(
          // Solo in questa fase "già stampato" significa "già stampato prima di stampare": lo intercettiamo qui
          // e non in fondo alla pipe, per non trasformare in successo lo stesso errore arrivato da fasi successive
          catchError((error) => this.catchAlreadyPrintedOnBuild$(error)),
          concatMap((context) => {
            if (!context) {
              result.isAlreadyPrinted = true;
              return of(result);
            }

            // Synchiamo la stampante, e aggiorniamo il context coi dati relativi
            return this.syncPrinter$(context).pipe(
              // Effettuiamo i controlli pre-stampa
              concatMap((syncedCtx) => this.prePrintCheck$(syncedCtx)),
              // Se è andato tutto bene fin'ora, incodiamo la stampa
              concatMap((checkedCtx) => this.enqueuePrint$(checkedCtx, progressive, block)),
              // Da qui in poi (in caso di successo dell'incodamento), abbiamo 2 possibili flussi
              concatMap((enqueuedCtx) => {
                // Se il documento non è fiscale, usciamo direttamente
                if (!isFiscal) {
                  return of(
                    this.continueFlow({
                      ...enqueuedCtx,
                      saveLinkedDataResult: true,
                    }),
                  );
                }

                // Altrimenti proviamo a salvare i dati fiscali
                return this.saveFiscalData$(enqueuedCtx, progressive, block, result);
              }),
              concatMap((flow) => {
                // Fine del flow, se è impostato come completato, usciamo
                if (flow.kind === 'completed') {
                  return of(flow.result);
                }

                // Altrimenti gestiamo la doppia copia, poi usciamo
                return this.handleDoubleCopy$(flow.context).pipe(map(() => result));
              }),
            );
          }),
        );
      }),
      catchError((error) => {
        if (error instanceof AppError) {
          return throwError(() => error);
        }

        return throwError(() =>
          new AppErrorBuilder('[FeatureCashRegisterService] Error printing receipt', Severity.ERROR)
            .addCause(error)
            .addInfo('invoiceId', invoiceId)
            .addInfo('zPrinterVersion', this.zPrinterVersion())
            .build(),
        );
      }),
    );
  }

  /**
   * Costruisce il contesto per la stampa
   */
  private buildPrintContext$(invoiceId: number, lang: SupportedLanguages): Observable<PrintReceiptContext> {
    return forkJoin({
      cashRegister: this.resolveCashRegister(invoiceId),
      receipt: this.buildCashRegisterReceipt(invoiceId, lang),
    }).pipe(
      map((data) => ({
        invoiceId,
        cashRegister: data.cashRegister,
        document: data.receipt,
      })),
    );
  }

  /**
   * In fase di build del documento, converte l'errore "documento già stampato" in `null`; rilancia gli altri
   */
  private catchAlreadyPrintedOnBuild$(error: unknown): Observable<null> {
    if (isAppErrorWithBackendCause(error, BackendErrorCode.DocumentAlreadyPrintedOnCashRegisterException)) {
      return of(null);
    }

    return throwError(() => error);
  }

  /**
   * Sincronizza la stampante
   */
  private syncPrinter$<T>(context: PrintDocumentContext<T>): Observable<PrintDocumentContext<T>> {
    return this.dataAccessZPrinterService.syncPrinter(this.mapCashRegisterToUpsertPrinter(context.cashRegister)).pipe(
      map((printer) => ({
        ...context,
        serialNumber: printer.serial_number,
      })),
    );
  }

  /**
   * Effettua i controlli pre-stampa
   */
  private prePrintCheck$(context: PrintReceiptContext): Observable<PrintReceiptContext> {
    /*
    Utilizziamo il metodo `printerStatus` che risponde rapidamente, al contrario di `status`
    che pur dando un risultato molto più accurato, non ha tempi di risposta sufficientemente
    bassi da essere usato in questo contesto.
   */
    return this.dataAccessZPrinterService.printerStatus(context.cashRegister.id.toString()).pipe(
      take(1),
      // In caso in cui la stampante non sia pronta, lanciamo un'eccezione
      tap((status) => this.throwIfPrinterNotReady(status)),
      map(() => context),
    );
  }

  /**
   * Mette il documento indicato in coda
   */
  private enqueuePrint$(
    context: PrintReceiptContext,
    progressive: number,
    block: string | null,
  ): Observable<PrintReceiptContext> {
    return this.enqueueDocument(
      context.cashRegister.id.toString(),
      context.document,
      context.invoiceId,
      progressive,
      block,
      // La marca serve a risolvere natura e indice IVA della riga come li vuole quella stampante
      this.mapManufacturer(context.cashRegister.cashRegisterType?.manufacturer),
    ).pipe(
      map((job) =>
        // Controlliamo l'esito anche in caso di successo, in quanto potrebbero esserci degli errori comunque
        this.checkJobResultForError(job, context.document, context.invoiceId, progressive, block),
      ),
      map((job) => ({
        ...context,
        job,
      })),
    );
  }

  /**
   * Effettua il salvataggio dei dati fiscali, gestendo eventualmente il documento già stampato
   */
  private saveFiscalData$(
    context: PrintReceiptContext,
    progressive: number,
    block: string | null,
    printReceiptResult: PrintReceiptResult,
  ): Observable<PrintReceiptFlow> {
    return this.savePrintedDocumentData(
      context.invoiceId,
      context.job?.number ?? null,
      context.job?.closure ?? null,
      context.serialNumber ?? null,
    ).pipe(
      map((saveLinkedDataResult): SaveFiscalDataOutcome => ({ alreadyPrinted: false, saveLinkedDataResult })),
      catchError((error) =>
        // Intercettiamo il caso "documento già stampato" PRIMA del retry, altrimenti retryWithBackoff
        // lo ritenterebbe inutilmente (non lo riconosce: isBackendError su AppError è sempre false).
        // Qui lo marchiamo soltanto: la gestione (dialog e stampa dell'annullo) avviene fuori dal retry,
        // così un errore dell'annullo non fa ripartire il salvataggio né riapre la dialog.
        isAppErrorWithBackendCause(error, BackendErrorCode.DocumentAlreadyPrintedOnCashRegisterException)
          ? of<SaveFiscalDataOutcome>({ alreadyPrinted: true })
          : throwError(() => error),
      ),
      retryWithBackoff({
        maxRetries: 5,
        delayMs: 3000,
        backoffFactor: 2,
        onRetry: (error, retryCount, maxRetries, waitTime) =>
          this.dialogService
            .showRetry({
              retryCounter: {
                retryCount,
                maxRetries,
              },
              msForNextTry: waitTime,
            })
            .pipe(
              take(1),
              filter((x) => x.actionType === ActionType.Confirm),
              map(() => void 0),
            ),
      }),
      catchError((error) => this.wrapSaveFiscalDataError$(error, context, progressive, block)),
      concatMap((outcome) =>
        outcome.alreadyPrinted
          ? this.handleAlreadyPrintedOnSave$(context, progressive, block, printReceiptResult)
          : of(
              this.continueFlow({
                ...context,
                saveLinkedDataResult: outcome.saveLinkedDataResult,
              }),
            ),
      ),
      tap(() => {
        this.eventBusService.publish(new SavedPrinterDocumentDataEvent(context.invoiceId));
      }),
    );
  }

  /**
   * Gestisce l'errore di documento già stampato in fase di salvataggio dei dati fiscali
   */
  private handleAlreadyPrintedOnSave$(
    context: PrintReceiptContext,
    progressive: number,
    block: string | null,
    printReceiptResult: PrintReceiptResult,
  ): Observable<PrintReceiptFlow> {
    const invoiceData = new InvoiceData();

    invoiceData.invoiceId = context.invoiceId;
    invoiceData.cashRegisterClosure = context.job?.closure?.toString() ?? null;
    invoiceData.cashRegisterCode = context.job?.number?.toString() ?? null;
    invoiceData.cashRegisterNumber = context.serialNumber ?? null;
    invoiceData.cashRegisterPrintTimestamp = today();

    return this.manageAlreadyPrintedDocumentWithCancelReceipt(
      context.invoiceId,
      progressive,
      block,
      invoiceData,
      printReceiptResult,
    ).pipe(map((result) => this.completedFlow(result)));
  }

  /**
   * Wrappa l'errore, aggiungendo i dati fiscali
   */
  private wrapSaveFiscalDataError$(
    error: Error | AppError,
    context: PrintReceiptContext,
    progressive: number,
    block: string | null,
  ): Observable<never> {
    return throwError(() =>
      new AppErrorBuilder(
        `[FeatureCashRegisterService] Error save printed document data. InvoiceId: ${context.invoiceId} - Progressive: ${progressive} - Block:${block}.`,
        Severity.ERROR,
      )
        .addCause(error)
        .addInfo('jobNumber', context.job?.number)
        .addInfo('jobClosure', context.job?.closure)
        .addInfo('serialNumber', context.serialNumber)
        .addInfo('zPrinterVersion', this.zPrinterVersion())
        .build(),
    );
  }

  /**
   * Gestisce la stampa della doppia copia
   */
  private handleDoubleCopy$<T>(context: PrintDocumentContext<T>): Observable<PrintDocumentContext<T>> {
    if (!context.saveLinkedDataResult) {
      return of(context);
    }

    if (!context.cashRegister.doubleCopy) {
      return of(context);
    }

    return this.reprintLastDocument(context.cashRegister.id.toString()).pipe(map(() => context));
  }

  private checkJobResultForError(
    result: ZPrinterPrintJob,
    cashRegisterReceipt: CashRegisterReceipt | CashRegisterCreditNote,
    invoiceId: number,
    progressive: number,
    block: string | null,
  ): ZPrinterPrintJob;

  private checkJobResultForError(
    result: PrintJobDtoEntity,
    cashRegisterReceipt: CashRegisterReceipt | CashRegisterCreditNote,
    invoiceId: number,
    progressive: number,
    block: string | null,
  ): PrintJobDtoEntity;

  private checkJobResultForError(
    result: ZPrinterPrintJob | PrintJobDtoEntity,
    cashRegisterReceipt: CashRegisterReceipt | CashRegisterCreditNote,
    invoiceId: number,
    progressive: number,
    block: string | null,
  ): ZPrinterPrintJob | PrintJobDtoEntity {
    if (result.status === ZPrinterTransactionStatus.Error || (result.errors && result.errors.length > 0)) {
      this.logPrintedDocument(
        `[FeatureCashRegisterService] Stampa documento in errore. InvoiceId: ${invoiceId} - Progressivo: ${progressive} - Blocco: ${block ?? ''}`,
        cashRegisterReceipt,
        result,
        progressive,
        block,
      );

      throw new AppErrorBuilder(`[FeatureCashRegisterService] Error while printing the document`)
        .addInfo('zPrinterVersion', this.zPrinterVersion())
        .addInfo('invoiceId', invoiceId)
        .addInfo('errors', JSON.stringify(result.errors))
        .build();
    }

    if (result.warnings && result.warnings.length > 0) {
      this.logPrintedDocumentWarning(
        `[FeatureCashRegisterService] Stampa documento in warning. InvoiceId: ${invoiceId} - Progressivo: ${progressive} - Blocco: ${block ?? ''}`,
        result,
        progressive,
        block,
      );

      // Warning non bloccanti: il documento è comunque stampato, ma l'IVA applicata potrebbe non
      // coincidere con quella attesa. Vanno mostrati all'operatore, non solo loggati (fonte: doc ZBridge)
      const vatWarningMessages = result.warnings
        .map((warning) => this.translatePrinterVatWarningCode(warning.code))
        .filter((message): message is string => message !== undefined);

      if (vatWarningMessages.length > 0) {
        this.toastService.showWarning(vatWarningMessages.join(' '));
      }
    }

    return result;
  }

  /**
   * Traduce un codice warning IVA restituito da ZBridge sulla stampa di un documento (Printer.Vat.*).
   * Fonte codici: documentazione ZBridge "Aliquote e nature IVA".
   */
  private translatePrinterVatWarningCode(code: string | null | undefined): string | undefined {
    switch (code) {
      case 'Printer.Vat.VatRateMismatch':
        return this.translation.vatRateMismatchWarning;
      case 'Printer.Vat.VatNatureMismatch':
        return this.translation.vatNatureMismatchWarning;
      case 'Printer.Vat.DepartmentUnknown':
        return this.translation.departmentUnknownWarning;
      case 'Printer.Vat.VatIndexUnknown':
        return this.translation.vatIndexUnknownWarning;
      case 'Printer.Vat.VatIndexIgnored':
        return this.translation.vatIndexIgnoredWarning;
      default:
        return undefined;
    }
  }

  /**
   * Metodo per la gestione del documento già stampato con richiesta di annullamento del documento appena stampato.
   */
  private manageAlreadyPrintedDocumentWithCancelReceipt(
    invoiceId: number,
    progressive: number,
    block: string | null,
    newInvoiceData: InvoiceData,
    printReceiptResult: PrintReceiptResult,
  ): Observable<PrintReceiptResult> {
    printReceiptResult.isAlreadyPrinted = true;
    printReceiptResult.reloadCurrentInvoice = true;

    // Carichiamo gli invoiceData precedenti
    return this.dataAccessInvoiceService.getInvoiceData(invoiceId).pipe(
      switchMap((alreadyPrintedInvoiceData: InvoiceData | null) => {
        if (!alreadyPrintedInvoiceData) {
          // Questo caso non dovrebbe mai capitare, vuol dire che abbiamo un documento che il BE da stampato, ma senza invoiceData
          throw new AppErrorBuilder('[FeatureCashRegisterService] Cannot find InvoiceData', Severity.ERROR)
            .addInfo('invoiceId', invoiceId)
            .addInfo('zPrinterVersion', this.zPrinterVersion())
            .build();
        }

        // Mostriamo la dialog per chiedere all'utente se vuole annullare l'ultimo documento o meno
        return this.showReceiptAlreadyPrintedDialogConfirmation(alreadyPrintedInvoiceData).pipe(
          take(1),
          switchMap((result) => {
            if (result.actionType === ActionType.Confirm) {
              // Logghiamo la scelta dell'utente
              this.logCancelReceipt(
                `[FeatureCashRegisterService] Conferma stampa documento di annullo. InvoiceId: ${invoiceId} - Progressivo: ${progressive} - Blocco: ${block ?? ''}`,
                progressive,
                block,
              );

              // Stampa della nota di credito direttamente dai dati arrivati dal backend nella chiamata precedente
              return this.printCancelReceipt(invoiceId, newInvoiceData, progressive, block).pipe(
                // Mostriamo una dialog di conferma di documento stampato, vista la delicatezza della cosa,
                // meglio informare l'utente il più possibile
                switchMap(() => this.showPrintedCancelReceiptConfirmation()),
                map(() => printReceiptResult),
              );
            }

            // Logghiamo la scelta dell'utente
            this.logCancelReceipt(
              `[FeatureCashRegisterService] Annullamento modale di stampa del documento di annullo. InvoiceId: ${invoiceId} - Progressivo: ${progressive} - Blocco: ${block ?? ''}`,
              progressive,
              block,
            );

            // Mostriamo una dialog di avviso di annullamento, vista la delicatezza della cosa,
            // meglio informare l'utente il più possibile
            return this.showCancelDialogConfirmation().pipe(map(() => printReceiptResult));
          }),
        );
      }),
    );
  }

  /**
   * Ristampa l'ultimo documento
   */
  public reprintLastDocument(printerIdOrIp: string): Observable<void> {
    return this.dataAccessZPrinterService.reprintLastDocument(printerIdOrIp);
  }

  /** Recupera lo stato di salute del servizio z-printer */
  public health(): Observable<ZPrinterHealthResponse> {
    return this.dataAccessZPrinterService.health();
  }

  /**
   * Verifica che ZBridge sia raggiungibile e non in stato degraded.
   * Emette void se il bridge è healthy o degraded, mostra il dialog e completa con EMPTY se è degraded o non raggiungibile.
   */
  private checkBridgeHealth(): Observable<void> {
    return this.health().pipe(
      catchError((error) => {
        console.error(
          new AppErrorBuilder('[FeatureCashRegisterService] ZBridge non raggiungibile', Severity.ERROR)
            .addCause(error)
            .build(),
        );
        this.showBridgeNotRunningDialog();
        return EMPTY;
      }),
      switchMap((healthResponse) => {
        if (healthResponse?.checks?.settings?.status === ZPrinterHealthStatus.Degraded) {
          this.featureLogService.warnExternal(
            new LogInfoBuilder('[FeatureCashRegisterService] ZBridge in stato degraded')
              .addInfo('checks', JSON.stringify(healthResponse.checks))
              .build(),
          );

          this.showBridgeNotConfiguredDialog();
        }
        return of(undefined);
      }),
    );
  }

  private showBridgeNotRunningDialog(): void {
    this.dialogService
      .showAlert({
        content: this.translation.bridgeNotRunning,
        cancelButtonText: undefined,
        confirmButtonText: this.translation.confirm,
      })
      .pipe(take(1))
      .subscribe();
  }

  private showMissingFiscalIndexConfigDialog(missingCashRegisterCode: string[]): void {
    this.dialogService
      .showAlert({
        content: this.translation.missingFiscalIndexConfig(missingCashRegisterCode.join(', ')),
        cancelButtonText: undefined,
        confirmButtonText: this.translation.confirm,
      })
      .pipe(take(1))
      .subscribe();
  }

  private showFiscalConfigAlert(content: string): void {
    this.dialogService
      .showAlert({
        content,
        cancelButtonText: undefined,
        confirmButtonText: this.translation.confirm,
      })
      .pipe(take(1))
      .subscribe();
  }

  private showUnsupportedNaturaConfigDialog(unsupportedNaturaNames: string[]): void {
    this.dialogService
      .showAlert({
        content: this.translation.unsupportedNaturaConfig(unsupportedNaturaNames.join(', ')),
        cancelButtonText: undefined,
        confirmButtonText: this.translation.confirm,
      })
      .pipe(take(1))
      .subscribe();
  }

  private showBridgeNotConfiguredDialog(): void {
    this.dialogService
      .showAlert({
        content: this.translation.bridgeNotConfigured,
        cancelButtonText: undefined,
        confirmButtonText: this.translation.confirm,
      })
      .pipe(take(1))
      .subscribe();
  }

  /**
   * La coppia chiusura e codice scontrino vanno a identificare univocamente lo scontrino. Dobbiamo stare attenti e controllare se la
   * chiusura è la corrente o la precedente
   *
   * @param documentNumber progressivo dello scontrino dall'ultima chiusura
   * @param cashRegisterClosure numero di chiusura corrente
   * @param cashRegisterNumber numero seriale della cassa
   */
  public savePrintedDocumentData(
    invoiceId: number,
    documentNumber: number | null,
    cashRegisterClosure: number | null,
    cashRegisterNumber: string | null,
  ): Observable<boolean> {
    if (!documentNumber || !cashRegisterClosure || !cashRegisterNumber) {
      throw new AppErrorBuilder(
        '[FeatureCashRegisterService] Invalid parameters for saving printed document data',
        Severity.ERROR,
      )
        .addInfo('invoiceId', invoiceId)
        .addInfo('zPrinterVersion', this.zPrinterVersion())
        .build();
    }

    const printedDocumentData = new SavePrintedDocumentData();
    printedDocumentData.invoiceId = invoiceId;
    printedDocumentData.closure = cashRegisterClosure.toString();
    printedDocumentData.documentNumber = documentNumber.toString();
    printedDocumentData.cashRegisterNumber = cashRegisterNumber.toString();

    return this.dataAccessInvoiceService.savePrintedDocumentData(printedDocumentData);
  }

  public printCreditNote(
    invoiceId: number,
    progressive: number,
    block: string | null,
    isFiscal: boolean,
  ): Observable<PrintReceiptResult> {
    const result = new PrintReceiptResult();

    return this.checkBridgeHealth().pipe(
      switchMap(() => this.isAlreadyPrinted(invoiceId)),
      switchMap((alreadyPrinted) => {
        if (alreadyPrinted) {
          result.isAlreadyPrinted = true;
          return of(result);
        }

        return this.buildCreditNoteContext$(invoiceId).pipe(
          // Vedi printReceipt: "già stampato" è intercettato solo in fase di build
          catchError((error) => this.catchAlreadyPrintedOnBuild$(error)),
          concatMap((ctx) => {
            if (!ctx) {
              result.isAlreadyPrinted = true;
              return of(result);
            }

            return this.syncPrinter$(ctx).pipe(
              concatMap((syncedCtx) => this.enqueueCreditNote$(syncedCtx, progressive, block)),
              concatMap((enqueuedCtx) => this.saveCreditNoteFiscalData$(enqueuedCtx, progressive, block, isFiscal)),
              concatMap((savedCtx) => this.handleDoubleCopy$(savedCtx)),
              map(() => result),
            );
          }),
        );
      }),
      catchError((error) => {
        if (error instanceof AppError) {
          return throwError(() => error);
        }

        return throwError(() =>
          new AppErrorBuilder('[FeatureCashRegisterService] Error printing credit note', Severity.ERROR)
            .addCause(error)
            .addInfo('invoiceId', invoiceId)
            .addInfo('zPrinterVersion', this.zPrinterVersion())
            .build(),
        );
      }),
    );
  }

  private buildCreditNoteContext$(invoiceId: number): Observable<PrintCreditNoteContext> {
    return forkJoin({
      cashRegister: this.resolveCashRegister(invoiceId),
      creditNote: this.buildCashRegisterCreditNote(invoiceId),
    }).pipe(
      map((data) => ({
        invoiceId,
        cashRegister: data.cashRegister,
        document: data.creditNote,
      })),
    );
  }

  /**
   * Mette in coda di stampa la nota di credito
   */
  private enqueueCreditNote$(
    context: PrintCreditNoteContext,
    progressive: number,
    block: string | null,
  ): Observable<PrintCreditNoteContext> {
    return this.enqueueCreditNote(
      context.cashRegister.id.toString(),
      context.document,
      context.invoiceId,
      progressive,
      block,
    ).pipe(
      map((job) => this.checkJobResultForError(job, context.document, context.invoiceId, progressive, block)),
      map((job) => ({
        ...context,
        job,
      })),
    );
  }

  /**
   * Effettua il salvataggio dei dati fiscali della nota di credito
   */
  private saveCreditNoteFiscalData$(
    context: PrintCreditNoteContext,
    progressive: number,
    block: string | null,
    isFiscal: boolean,
  ): Observable<PrintCreditNoteContext> {
    if (!isFiscal) {
      return of({
        ...context,
        saveLinkedDataResult: true,
      });
    }

    if (!context.invoiceId) {
      return of({
        ...context,
        saveLinkedDataResult: false,
      });
    }

    return this.savePrintedDocumentData(
      context.invoiceId,
      context.job?.number ?? null,
      context.job?.closure ?? null,
      context.serialNumber ?? null,
    ).pipe(
      tap(() => {
        this.eventBusService.publish(new SavedPrinterDocumentDataEvent(context.invoiceId));
      }),
      retryWithBackoff({
        maxRetries: 5,
        delayMs: 3000,
        backoffFactor: 2,
        onRetry: (error, retryCount, maxRetries, waitTime) =>
          this.dialogService
            .showRetry({
              retryCounter: {
                retryCount,
                maxRetries,
              },
              msForNextTry: waitTime,
            })
            .pipe(
              take(1),
              filter((x) => x.actionType === ActionType.Confirm),
              map(() => void 0),
            ),
      }),
      map((saveLinkedDataResult) => ({
        ...context,
        saveLinkedDataResult,
      })),
      catchError((error) =>
        throwError(() =>
          new AppErrorBuilder(
            `[FeatureCashRegisterService] Error save printed document data. InvoiceId: ${context.invoiceId} - Progressive: ${progressive} - Block:${block}.`,
            Severity.ERROR,
          )
            .addCause(error)
            .addInfo('jobNumber', context.job?.number)
            .addInfo('jobClosure', context.job?.closure)
            .addInfo('serialNumber', context.serialNumber)
            .addInfo('zPrinterVersion', this.zPrinterVersion())
            .build(),
        ),
      ),
    );
  }

  public printCancelReceipt(
    invoiceId: number,
    invoiceDataToCancel: InvoiceData,
    progressive: number,
    block: string | null,
  ): Observable<void> {
    // Richiedo su quale stampante fiscale stampare
    const cashRegister$ = this.resolveCashRegister(invoiceId);
    // Chiedo al backend di costruire la nota di credito a partire dall'invoice
    const cashRegisterCreditNote$ = this.buildCashRegisterCreditNoteFromInvoiceData(invoiceDataToCancel);

    return forkJoin([cashRegister$, cashRegisterCreditNote$]).pipe(
      concatMap(([cashRegister, cashRegisterCreditNote]) =>
        this.dataAccessZPrinterService.syncPrinter(this.mapCashRegisterToUpsertPrinter(cashRegister)).pipe(
          map((updatePrinterResult) => {
            return {
              cashRegister: cashRegister,
              cashRegisterCreditNote: cashRegisterCreditNote,
              serialNumber: updatePrinterResult.serial_number,
            };
          }),
        ),
      ),
      concatMap((updatePrinterResult) =>
        this.enqueueCreditNote(
          updatePrinterResult.cashRegister.id.toString(),
          updatePrinterResult.cashRegisterCreditNote,
          invoiceId,
          progressive,
          block,
        ).pipe(map(() => void 0)),
      ),
      catchError((error) => {
        throw new AppErrorBuilder('[FeatureCashRegisterService] Error printing cancel receipt', Severity.ERROR)
          .addCause(error)
          .addInfo('invoiceId', invoiceId)
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  /**
   * Indica se il documento è già stato stampato
   * @param invoiceId
   */
  public isAlreadyPrinted(invoiceId: number): Observable<boolean> {
    return this.dataAccessInvoiceService.isAlreadyPrinted(invoiceId).pipe(
      take(1),
      map((alreadyPrinted) => {
        if (!alreadyPrinted) {
          return false;
        }

        return true;
      }),
      switchMap((result) => {
        if (result) {
          // Se il documento è già stato stampato, carichiamo e visualizziamo i dati
          return this.loadAndShowAlreadyPrintedDocumentData(invoiceId);
        }

        return of(result);
      }),
    );
  }

  /**
   * Costruisce e invia la fiscal config alla cassa tramite ZPrinterGateway
   */
  public sendFiscalConfig(cashRegister: CashRegister): Observable<ZPrinterFiscalProtocol[]> {
    // Indica se la chiusura fiscale che precede l'invio è già stata eseguita: se l'invio poi fallisce, l'utente deve saperlo, perché la chiusura è irreversibile e non va nascosta dietro un generico "impossibile inviare".
    let isClosureDone = false;

    return forkJoin({
      departments: this.dataAccessDepartmentService.getAllDepartment(),
      payments: this.dataAccessCashRegisterService.getCashRegisterPayment(),
    }).pipe(
      switchMap(({ departments, payments }) => {
        const manufacturer = this.mapManufacturer(cashRegister.cashRegisterType?.manufacturer);

        // Contano solo le aliquote usate dai reparti: l'IVA di una riga la decide il reparto, quindi un'aliquota non collegata a nessun reparto non verrebbe mai usata in vendita e non deve bloccare la configurazione
        // (es. una N7 pensata per la fatturazione elettronica).
        const vatRates = getVatRatesUsedByDepartments(departments);

        // Reparti e aliquote senza codice registratore di cassa produrrebbero un index non valido:
        // blocchiamo l'invio piuttosto che mandare una configurazione fiscale incompleta alla stampante.
        // Le aliquote "natura" su una casella fissa (Epson/Custom/ItalRetail) non usano cashRegisterCode,
        // l'indice viene calcolato più sotto in base alla marca; su RCH invece serve anche per le nature,
        // dato che lì l'indice è libero come per le aliquote percentuali (vedi z-printer-natura-mapping.ts)
        const requiresCashRegisterCode = (vatRate: VatRate): boolean =>
          !vatRate.naturaTypeOnZeroVat || manufacturer === ZPrinterManufacturer.Rch;

        const missingCashRegisterCode = [
          ...departments.filter((d) => !d.cashRegisterCode).map((d) => d.description),
          ...vatRates.filter((v) => requiresCashRegisterCode(v) && !v.cashRegisterCode).map((v) => v.description),
        ];

        if (missingCashRegisterCode.length > 0) {
          this.showMissingFiscalIndexConfigDialog(missingCashRegisterCode);
          return EMPTY;
        }

        // Le nature che restano non risolvibili (marca senza mappatura documentata, o RCH con una natura
        // che il suo protocollo non riconosce, es. N7) bloccano l'invio invece di mandare un indice inventato alla stampante fisica
        const unsupportedNatura = vatRates
          .filter((v): v is typeof v & { naturaTypeOnZeroVat: NaturaType } => v.naturaTypeOnZeroVat != null)
          .filter((v) => resolveExpectedVatIndex(v, manufacturer) === undefined)
          .map((v) => v.description);

        if (unsupportedNatura.length > 0) {
          this.showUnsupportedNaturaConfigDialog(unsupportedNatura);
          return EMPTY;
        }

        // Conflitti che si vedono già dai dati in memoria: l'invio esegue prima una chiusura fiscale irreversibile e solo dopo ZBridge può rifiutare la configurazione, quindi conviene fermarsi qui invece di bruciare una chiusura per niente
        const conflicts = findFiscalConfigConflicts(departments, vatRates, manufacturer);

        if (conflicts.departmentCodes.length > 0) {
          this.showFiscalConfigAlert(this.translation.duplicateDepartmentCodes(conflicts.departmentCodes.join('; ')));
          return EMPTY;
        }

        if (conflicts.vatIndexes.length > 0) {
          this.showFiscalConfigAlert(this.translation.duplicateVatIndexes(conflicts.vatIndexes.join('; ')));
          return EMPTY;
        }

        if (conflicts.vatPercentages.length > 0) {
          this.showFiscalConfigAlert(this.translation.duplicateVatPercentages(conflicts.vatPercentages.join('; ')));
          return EMPTY;
        }

        const request = new ZPrinterFiscalConfigRequest();
        request.fromSources(cashRegister, departments, vatRates, payments, manufacturer);

        const printerId = cashRegister.id.toString();

        // Pre-flight: l'invio della fiscal-config esegue prima una chiusura fiscale reale e irreversibile.
        // Su alcuni modelli (es. Custom) la tabella IVA non è programmabile da remoto e il POST fallirebbe comunque, ma solo dopo aver bruciato quella chiusura.
        // La stampante lo dichiara già in lettura, quindi chiediamoglielo prima: se dice che la sezione è fissa o assente, ci fermiamo qui.
        return this.dataAccessZPrinterService.getFiscalConfig(printerId).pipe(
          // Questa lettura è solo un controllo in più, non un requisito per inviare
          // Quindi non blocchiamo: continuiamo con `null` ("nessuna informazione") e se la stampante è davvero irraggiungibile fallirà comunque la chiusura fiscale che precede l'invio, prima di scrivere qualsiasi cosa.
          catchError((error: unknown) => {
            console.error(
              new AppErrorBuilder(
                '[FeatureCashRegisterService] Lettura preventiva della fiscal config non riuscita, si procede comunque',
                Severity.WARNING,
              )
                .addCause(error instanceof Error ? error : new Error(String(error)))
                .addInfo('cashRegisterId', cashRegister.id)
                .build(),
            );
            return of(null);
          }),
          switchMap((printerConfig) => {
            // Si controllano solo le IVA, di proposito: sono le uniche per cui "fissa o non esposta" significa davvero "non programmabile".
            // Per pagamenti e reparti no, e bloccare impedirebbe configurazioni che oggi funzionano:
            // - pagamenti: su Epson la sezione è `read_only` ma l'invio va a buon fine (la stampante li ignora e programma IVA e reparti)
            // - reparti: su Custom il protocollo non li restituisce in lettura, ma si scrivono normalmente
            // Non poter leggere una sezione non vuol dire non poterla scrivere.
            if (printerConfig && (!printerConfig.vats.is_applicable || printerConfig.vats.read_only)) {
              this.dialogService.showDanger({
                content: this.translation.fiscalConfigErrorOperationNotSupported,
              });
              return EMPTY;
            }

            // La chiusura fiscale precede sempre la programmazione, su tutte le marche.
            // I due passi sono separati per sapere, se la programmazione fallisce, che la chiusura è già avvenuta
            isClosureDone = false;
            return this.dataAccessZPrinterService.sendClosure(printerId).pipe(
              tap(() => {
                isClosureDone = true;
              }),
              switchMap(() => this.dataAccessZPrinterService.setFiscalConfig(printerId, request)),
            );
          }),
        );
      }),
      tap(() => {
        this.toastService.showSuccess();
      }),
      catchError((error: unknown) => {
        // Non è detto che qui arrivi un AppError: un normale Error può nascere anche prima dell'invio, ad esempio
        // dai controlli dentro `fromSources` (codice reparto non numerico, tipo di pagamento non mappato...).
        // Un Error normale non ha `info`, quindi tradurlo con translateFiscalConfigErrorCode lancerebbe un TypeError
        // al posto dell'errore vero.
        const cause = error instanceof Error ? error : new Error(String(error));

        // ZBridge rifiuta una fiscal-config incoerente prima di scriverla: se riconosciamo il codice
        // errore mostriamo un messaggio utile invece del fallback generico sotto
        const knownErrorMessage = error instanceof AppError ? this.translateFiscalConfigErrorCode(error) : undefined;
        if (knownErrorMessage) {
          // L'utente vede un messaggio chiaro e non serve l'errore generico, ma l'evento deve restare visibile al monitoring
          this.featureLogService.warnExternal(
            new AppErrorBuilder('[FeatureCashRegisterService] Fiscal config rifiutata da ZBridge', Severity.WARNING)
              .addCause(cause)
              .addInfo('cashRegisterId', cashRegister.id)
              .addInfo('isClosureDone', isClosureDone)
              .addInfo('zPrinterVersion', this.zPrinterVersion())
              .build(),
          );

          this.showFiscalConfigAlert(
            isClosureDone ? this.translation.fiscalConfigErrorAfterClosure(knownErrorMessage) : knownErrorMessage,
          );
          return EMPTY;
        }

        // La chiusura è già stata eseguita ma l'invio è fallito per un motivo che non conosciamo:
        //  l'utente va avvisato comunque, altrimenti penserebbe che non sia successo nulla.
        // L'errore resta tracciato, ma senza rilanciarlo, per non mostrare anche il messaggio generico sopra a quello che spiega cosa è successo davvero
        if (isClosureDone) {
          this.featureLogService.errorExternal(
            new AppErrorBuilder(
              '[FeatureCashRegisterService] Error sending fiscal config after the fiscal closure',
              Severity.ERROR,
            )
              .addCause(cause)
              .addInfo('cashRegisterId', cashRegister.id)
              .addInfo('zPrinterVersion', this.zPrinterVersion())
              .build(),
          );

          this.showFiscalConfigAlert(this.translation.fiscalConfigErrorAfterClosureUnknown);
          return EMPTY;
        }

        // Un errore che non è un AppError lo rilanciamo così com'è, mantenendo messaggio e stack originali
        if (!(error instanceof AppError)) {
          return throwError(() => error);
        }

        throw new AppErrorBuilder('[FeatureCashRegisterService] Error sending fiscal config', Severity.ERROR)
          .addCause(error)
          .addInfo('cashRegisterId', cashRegister.id)
          .addInfo('zPrinterVersion', this.zPrinterVersion())
          .build();
      }),
    );
  }

  /**
   * Traduce un codice errore restituito da ZBridge sull'invio della fiscal-config.
   *
   * ZBridge usa due forme di errore diverse:
   * gli errori di dominio arrivano come 409 con { code, description, type, metadata }, mentre gli errori di validazione arrivano come 400 con
   * { message, field }. Il codice sta nel primo campo disponibile tra i due.
   */
  private translateFiscalConfigErrorCode(error: AppError): string | undefined {
    const responseBody = error.info.get('responseBody');
    if (typeof responseBody !== 'string') return undefined;

    let code: string | null | undefined;
    try {
      const body = JSON.parse(responseBody) as Partial<ZPrinterError & ValidationErrorResponse>;
      code = body.code ?? body.message;
    } catch {
      return undefined;
    }

    switch (code) {
      case 'Printer.Driver.Domain.VatNatureNotSupported':
        return this.translation.fiscalConfigErrorVatNatureNotSupported;
      case 'Printer.Driver.Domain.VatIndexNotValid':
        return this.translation.fiscalConfigErrorVatIndexNotValid;
      case 'Printer.Driver.Domain.VatNatureNotProgrammable':
        return this.translation.fiscalConfigErrorVatNatureNotProgrammable;
      case 'Printer.Driver.Infrastructure.FiscalConfigurationReadFailed':
        return this.translation.fiscalConfigErrorReadFailed;
      // Verificato su una Custom reale: il driver non supporta né la scrittura della singola voce IVA
      // né la lettura della tabella richiesta prima di riscriverla, quindi la tabella IVA non è programmabile da remoto su quel modello
      case 'Printer.Driver.Infrastructure.OperationNotSupportedForOperation':
        return this.translation.fiscalConfigErrorOperationNotSupported;
      default:
        return undefined;
    }
  }

  /**
   * Effettua il caricamento dei dati del documento già stampato e li mostra all'utente
   */
  public loadAndShowAlreadyPrintedDocumentData(invoiceId: number): Observable<boolean> {
    return this.dataAccessInvoiceService.getInvoiceData(invoiceId).pipe(
      take(1),
      switchMap((alreadyPrintedInvoiceData) => {
        if (!alreadyPrintedInvoiceData) {
          throw new AppErrorBuilder('[FeatureCashRegisterService] Cannot find InvoiceData', Severity.ERROR)
            .addInfo('invoiceId', invoiceId)
            .addInfo('zPrinterVersion', this.zPrinterVersion())
            .build();
        }

        return this.showReceiptAlreadyPrintedDialog(alreadyPrintedInvoiceData).pipe(map(() => true));
      }),
    );
  }

  /**
   * Mostra la dialog di conferma di documento già stampato, con gli estremi del documento precedente e la possibilità di annullarlo
   */
  public showReceiptAlreadyPrintedDialogConfirmation(
    alreadyPrintedInvoiceData: InvoiceData,
  ): Observable<OverlayResult> {
    return this.dialogService.showDanger({
      content: this.translation.documentAlreadyPrintedCancelQuestion(
        alreadyPrintedInvoiceData.cashRegisterCode,
        alreadyPrintedInvoiceData.cashRegisterClosure,
        alreadyPrintedInvoiceData.cashRegisterNumber,
      ),
      cancelButtonText: this.translation.cancel,
      confirmButtonText: this.translation.cancelCurrentReceipt,
    });
  }

  /**
   * Mostra la dialog di avviso di documento già stampato, con gli estremi del documento precedente
   */
  public showReceiptAlreadyPrintedDialog(alreadyPrintedInvoiceData: InvoiceData): Observable<OverlayResult> {
    return this.dialogService.showDanger({
      content: this.translation.documentAlreadyPrinted(
        alreadyPrintedInvoiceData.cashRegisterCode,
        alreadyPrintedInvoiceData.cashRegisterClosure,
        alreadyPrintedInvoiceData.cashRegisterNumber,
      ),
      cancelButtonText: undefined,
      confirmButtonText: this.translation.confirm,
    });
  }

  /**
   * Mostra la dialog di conferma di stampa del documento di annullo
   */
  public showPrintedCancelReceiptConfirmation(): Observable<OverlayResult> {
    return this.dialogService.showConfirm({
      content: this.translation.cancelDocumentPrinted,
      cancelButtonText: undefined,
      disableCloseOnEsc: true,
      disableCloseOnOverlayClick: true,
    });
  }

  /**
   * Mostra la dialog di conferma della scelta di non annullare il documento già stampato
   */
  public showCancelDialogConfirmation(): Observable<OverlayResult> {
    return this.dialogService.showInfo({
      content: this.translation.cancelDocumentNotPrinted,
      cancelButtonText: undefined,
      disableCloseOnEsc: true,
      disableCloseOnOverlayClick: true,
    });
  }

  /** Logga la scelta dell'utente durante la gestione del documento di annullo */
  private logCancelReceipt(message: string, progressive: number, block: string | null): void {
    const logInfo = new LogInfoBuilder(message).addInfo('progressive', progressive);
    if (block) logInfo.addInfo('block', block);
    this.featureLogService.logExternal(logInfo.build());
  }

  private logPrintedDocument(
    message: string,
    backendResponse: CashRegisterReceipt | CashRegisterCreditNote,
    zPrinterJob: ZPrinterPrintJob | PrintJobDtoEntity,
    progressive: number,
    block: string | null,
  ): void {
    const logInfo = new LogInfoBuilder(message)
      .addInfo('BackEnd Response', JSON.stringify(backendResponse))
      .addInfo('document', JSON.stringify(zPrinterJob.transaction?.document) ?? undefined)
      .addInfo('job', JSON.stringify(zPrinterJob) ?? '')
      .addInfo('progressive', progressive);

    if (block) logInfo.addInfo('block', block);

    this.featureLogService.logExternal(logInfo.build());
  }

  private logPrintedDocumentWarning(
    message: string,
    zPrinterJob: ZPrinterPrintJob | PrintJobDtoEntity,
    progressive: number,
    block: string | null,
  ): void {
    const logInfo = new LogInfoBuilder(message)
      .addInfo('warnings', JSON.stringify(zPrinterJob.warnings) ?? undefined)
      .addInfo('progressive', progressive);

    if (block) logInfo.addInfo('block', block);

    this.featureLogService.logExternal(logInfo.build());
  }

  public async loadZPrinterVersion(): Promise<void> {
    try {
      const version = await firstValueFrom(
        this.dataAccessZPrinterService.getVersion().pipe(timeout(ZBRIDGE_VERSION_TIMEOUT_MS)),
      );
      this._zPrinterVersion.set(version);
    } catch {
      this._zPrinterVersion.set(null);
    }
  }

  /**
   * Controlla se la versione installata di ZPrinter è compatibile con la versione minima indicata
   */
  public checkVersionSupported(versionToCheck: string): boolean {
    const zPrinterVersion = this.zPrinterVersion();
    if (!zPrinterVersion) {
      this.dialogService.showAlert({ content: this.translation.bridgeNotInstalled });
      return false;
    }

    if (!isVersionSupported(zPrinterVersion, versionToCheck)) {
      this.dialogService.showAlert({
        content: this.translation.bridgeNotUpdated,
      });
      return false;
    }

    return true;
  }

  public formatCashRegisterErrors(error: AppError): string {
    const jsonErrors = error.info.get('errors') as string;
    if (!jsonErrors) return '';

    try {
      const errors = JSON.parse(jsonErrors) as ZPrinterError[];
      return errors.map((e) => e.description).join('<br>');
    } catch {
      return '';
    }
  }

  private continueFlow(context: PrintReceiptContext): ContinueFlow {
    return {
      kind: 'continue',
      context,
    };
  }

  private completedFlow(result: PrintReceiptResult): CompletedFlow {
    return {
      kind: 'completed',
      result,
    };
  }

  private throwIfPrinterNotReady(printerStatus: ZPrinterStatus): void {
    let errorTranslationKey: UserFacingTranslationKey | undefined;

    if (printerStatus.is_cover_open) {
      errorTranslationKey = 'ZPrinter.Dialog.IsCoverOpen';
    } else if (printerStatus.is_paper_nearly_empty) {
      errorTranslationKey = 'ZPrinter.Dialog.IsPaperNearlyEmpty';
    } else if (printerStatus.is_paper_over) {
      errorTranslationKey = 'ZPrinter.Dialog.IsPaperOver';
    }

    if (!errorTranslationKey) {
      return;
    }

    this.dialogService
      .showAlert({
        content: this.translateService.translateError(errorTranslationKey),
        cancelButtonText: undefined,
        confirmButtonText: $localize`:@@common.button.close:Chiudi`,
      })
      .pipe(take(1))
      .subscribe();

    throw new AppErrorBuilder(
      `[FeatureCashRegisterService] Pre-print check failed: ${errorTranslationKey}`,
      Severity.ERROR,
    )
      .addInfo('is_cover_open', printerStatus.is_cover_open)
      .addInfo('is_paper_nearly_empty', printerStatus.is_paper_nearly_empty)
      .addInfo('is_paper_over', printerStatus.is_paper_over)
      .addInfo('zPrinterVersion', this.zPrinterVersion())
      .build();
  }
}
